import {
  Button,
  Checkbox,
  Drawer,
  Input,
  Label,
  Loading,
  Textarea,
  Title,
} from "@components";
import { AssetsAPI } from "providers/AssetsAPI";
import { EnvironmentsAPI } from "providers/EnvironmentsAPI";
import { OnboardingAPI } from "providers/OnboardingAPI";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ForEach, If } from "shared/components/control-statements";
import {
  InfoCircle,
  InfoCircleGreen,
  InfoCircleGrey,
  Interrogation,
  Link,
} from "shared/components/Icons";
import { InputSearch } from "shared/components/InputSearch";
import { useDragScroll } from "shared/hooks/useDragScroll";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";
import styles from "styles/onboarding.module.scss";
import { v4 as uuid } from "uuid";

function isUUID(value) {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
}

export default function OnboardingBrands() {
  const environment = EnvironmentStorage.getEnvironment();

  const [search, setSearch] = useState("");
  const [brands, setBrands] = useState([]);
  const [hideCompletedBrands, setHideCompletedBrands] = useState(false);
  const [openDescription, setOpenDescription] = useState(false);
  const [openCopyDescription, setOpenCopyDescription] = useState(false);
  const [openCopyLink, setOpenCopyLink] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [title, setTitle] = useState("");

  const navigate = useNavigate();

  const { scrollRef, onMouseDown, wasDragging } = useDragScroll();

  const emptyBrand = {
    id: uuid(),
    logo: null,
    name: "",
    link: "",
    description: "",
  };

  const validateImageSize = (file, brand) => {
    if (!file) {
      alert({ text: "Arquivo inválido." });
      return false;
    }

    try {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const widthImage = img.naturalWidth;
        const heightImage = img.naturalHeight;

        if (widthImage !== 500 || heightImage !== 500) {
          alert({
            text: `Dimensões da imagem estão incorretas. Deve ser ${500}x${500}`,
          });
          return false;
        }
        sendFile(file, brand);
      };
    } catch (err) {
      alert({ text: "Erro ao processar a imagem." });
      return false;
    }
  };

  const sendFile = async (file, brand) => {
    const maxFileSize = 1;
    const fileSize = file.size / 1024 / 1024;
    const fileType = file.type;

    if (fileSize > maxFileSize) {
      alert({
        text: `Escolha um arquivo com no máximo ${maxFileSize}MB`,
      });
      return false;
    }

    const pattern = /image-*/;

    if (!fileType.match(pattern)) {
      alert({ text: "Arquivo inválido." });
      return false;
    }

    let reader = new FileReader();

    reader.onload = async (readerEvent) => {
      const response = await AssetsAPI.PostAssets({
        mime_type: fileType,
        file: readerEvent.target.result.split("base64,")[1],
      });

      setBrands((prevBrands) =>
        prevBrands.map((item) => {
          if (item.id === brand.id)
            return { ...item, logo: response.getData({}) };

          return item;
        })
      );
    };

    reader.readAsDataURL(file);
  };

  const handleSetImage = async (e, brand) => {
    const file = e.target.files[0];
    validateImageSize(file, brand);
  };

  const validLinkRegex = /^(https?:\/\/)?([\w-]+\.)+(com|com\.br)(\/.*)?$/i;

  const getCompletedBrandsPercentage = () => {
    if (!Array.isArray(brands) || brands.length === 0) return 0;

    const requiredFields = ["logo", "name", "description"];
    const totalFields = brands.length * requiredFields.length;

    let filledCount = 0;

    for (const item of brands) {
      for (const field of requiredFields) {
        const value = item[field];
        if (!value) continue;

        if (field === "link") {
          if (validLinkRegex.test(value)) filledCount++;
        } else {
          filledCount++;
        }
      }
    }

    const percentage = (filledCount / totalFields) * 100;
    return Number(percentage.toFixed(0));
  };

  const filteredBrands = brands.filter((brand) => {
    return brand.name.toLowerCase().includes(search.toLowerCase());
  });

  function getIncompleteObjects(data) {
    if (!Array.isArray(data)) return [];

    const requiredFields = ["logo", "name", "description"];

    return data.filter((item) => {
      for (const field of requiredFields) {
        const value = item[field];
        if (!value) return true;
        if (field === "link" && !validLinkRegex.test(value)) return true;
      }
      return false;
    });
  }

  const finalBrands = hideCompletedBrands
    ? getIncompleteObjects(filteredBrands)
    : filteredBrands;

  function updateObjectsByIds(data, selectedItems, field, newValue) {
    if (!Array.isArray(data) || !Array.isArray(selectedItems) || !field)
      return data;

    return data.map((item) =>
      selectedItems.includes(item.id) ? { ...item, [field]: newValue } : item
    );
  }

  const Items = () => {
    return (
      <div className={styles.itemsContainer}>
        <div className={styles.header}>
          <p>{selectedItems.length} selecionado(s)</p>
          <Checkbox
            checked={selectedItems.length === brands.length}
            onChange={() => {
              const filtered = brands.filter((b) => b.id !== selectedBrand?.id);

              if (selectedItems.length === filtered.length)
                setSelectedItems([]);
              else setSelectedItems(filtered.map((b) => b.id));
            }}
          />
        </div>
        <ForEach
          items={brands.filter((b) => b.id !== selectedBrand?.id)}
          render={(item) => (
            <div
              className={styles.brand}
              onClick={() => {
                if (selectedItems.includes(item.id))
                  setSelectedItems((prev) => prev.filter((b) => b !== item.id));
                else setSelectedItems((prev) => [...prev, item.id]);
              }}
            >
              <Checkbox checked={selectedItems.includes(item.id)} />
              <If cond={item.logo}>
                <div className={styles.logo}>
                  <img
                    src={process.env.REACT_APP_ASSETS_URL + "/" + item.logo}
                    alt={item.name}
                  />
                </div>
              </If>
              <If cond={!item.logo}>
                <div className={styles.logo}></div>
              </If>
              <p className={styles.brandName}>{item.name}</p>
            </div>
          )}
        />
      </div>
    );
  };

  const getBrands = async () => {
    const response = (await OnboardingAPI.getBrands()) || [];
    setBrands(response || []);
    setLoading(false);
  };

  const save = async () => {
    setLoading(true);

    const brandsWithoutId = brands.map((b) => {
      if (isUUID(b.id)) delete b.id;
      return b;
    });

    const response = await OnboardingAPI.putBrands(brandsWithoutId);

    if (response.getErrors({}).length > 0) {
      setOpenError(true);
      setErrorMessage(response.getErrors({})[0]);
    } else {
      setOpenSuccess(true);
      getBrands();
    }

    setLoading(false);
  };

  useEffect(() => {
    getBrands();
  }, []);

  const handleBrandChange = (brandId, field, value) => {
    setBrands((prevBrands) => {
      const newUnits = [...prevBrands];
      const index = newUnits.findIndex((b) => b.id === brandId);
      if (index !== -1) newUnits[index][field] = value;

      return newUnits;
    });
  };

  const getHolding = async () => {
    const environmentId = await EnvironmentStorage.getEnvironmentHoldingId();

    const holdingResponse = await EnvironmentsAPI.getById(environmentId);
    const holding = holdingResponse.getData({});
    if (holding.name) setTitle(holding.name || environment?.brand?.name);
  };

  useEffect(() => {
    getHolding();
  }, []);

  const shouldShowField = (brands, fieldPath) => {
    const getNestedValue = (obj, path) =>
      path.split(".").reduce((acc, key) => acc?.[key], obj);

    const allFilled = brands.every((b) => getNestedValue(b, fieldPath));
    const someEmpty = brands.some((b) => !getNestedValue(b, fieldPath));

    if (allFilled && hideCompletedBrands) return false;
    if (someEmpty) return true;

    return true;
  };

  return (
    <div className={styles.onboardingComponent}>
      <If cond={loading}>
        <Loading onboarding />
      </If>
      <div className={styles.onboardingHeader}>
        <div className={styles.content}>
          <Title title="Marcas" backTo={{ name: title, url: "/implantacao" }}>
            <div className="d-flex align-center gap-10">
              <If cond={brands.length}>
                <InputSearch
                  placeholder="Buscar por..."
                  onChange={(e) => setSearch(e.target.value)}
                  design="four"
                />
                <Button
                  className="gap-10"
                  onClick={() => setHideCompletedBrands(!hideCompletedBrands)}
                >
                  <If cond={!hideCompletedBrands}>
                    <i className="fal fa-eye" />
                    <span>Ocultar preenchidos</span>
                  </If>
                  <If cond={hideCompletedBrands}>
                    <i className="fal fa-eye-slash" />
                    <span>Mostrar preenchidos</span>
                  </If>
                </Button>
              </If>

              {/* <Interrogation /> */}
            </div>
            <div className={styles.divider} />
            <div className={styles.completeness}>
              <div
                className={styles.total}
                style={{ "--percent": getCompletedBrandsPercentage() }}
              >
                <span>{getCompletedBrandsPercentage()}%</span>
              </div>
            </div>
          </Title>
        </div>
      </div>

      <div className={styles.onboardingContainer}>
        <div onMouseDown={onMouseDown} ref={scrollRef} className={styles.table}>
          <table>
            <thead>
              <tr>
                <If cond={shouldShowField(brands, "logo")}>
                  <th
                    style={{
                      minWidth: 100,
                      position: "sticky",
                      left: 0,
                      zIndex: 200,
                      paddingLeft: 45,
                    }}
                  >
                    <div className="d-flex align-center justify-center gap-10">
                      LOGOTIPO
                      <div
                        style={{ cursor: "pointer" }}
                        title="Resolução 500x500, peso máximo 1MB"
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6.00039 1.10039C7.29995 1.10039 8.54629 1.61664 9.46521 2.53557C10.3841 3.4545 10.9004 4.70083 10.9004 6.00039C10.9004 7.29995 10.3841 8.54629 9.46521 9.46521C8.54629 10.3841 7.29995 10.9004 6.00039 10.9004C4.70083 10.9004 3.4545 10.3841 2.53557 9.46521C1.61664 8.54629 1.10039 7.29995 1.10039 6.00039C1.10039 4.70083 1.61664 3.4545 2.53557 2.53557C3.4545 1.61664 4.70083 1.10039 6.00039 1.10039ZM6.00039 11.6004C7.4856 11.6004 8.90999 11.0104 9.96019 9.96019C11.0104 8.90999 11.6004 7.4856 11.6004 6.00039C11.6004 4.51518 11.0104 3.0908 9.96019 2.04059C8.90999 0.990389 7.4856 0.400391 6.00039 0.400391C4.51518 0.400391 3.0908 0.990389 2.04059 2.04059C0.990389 3.0908 0.400391 4.51518 0.400391 6.00039C0.400391 7.4856 0.990389 8.90999 2.04059 9.96019C3.0908 11.0104 4.51518 11.6004 6.00039 11.6004ZM4.60039 8.10039V8.80039H4.95039H7.05039H7.40039V8.10039H7.05039H6.35039V5.65039V5.30039H6.00039H5.12539H4.77539V6.00039H5.12539H5.65039V8.10039H4.95039H4.60039ZM6.52539 4.42539V3.37539H5.47539V4.42539H6.52539Z"
                            fill="#6F767E"
                          />
                        </svg>
                      </div>
                    </div>
                  </th>
                </If>
                <th
                  style={{
                    minWidth: 300,
                    paddingLeft: shouldShowField(brands, "logo") ? 15 : 45,
                  }}
                >
                  NOME DA MARCA
                </th>
                <If cond={shouldShowField(brands, "link")}>
                  <th
                    style={{
                      minWidth: 300,
                    }}
                  >
                    LINK DO SITE
                  </th>
                </If>
                <If cond={shouldShowField(brands, "description")}>
                  <th
                    style={{
                      minWidth: 400,
                    }}
                  >
                    DESCRIÇÃO
                  </th>
                </If>
              </tr>
            </thead>
            <tbody>
              <ForEach
                items={finalBrands}
                render={(brand) => (
                  <tr key={brand.id}>
                    <If cond={shouldShowField(brands, "logo")}>
                      <td
                        style={{
                          minWidth: 100,
                          position: "sticky",
                          left: 0,
                          zIndex: 200,
                          paddingLeft: 45,
                        }}
                      >
                        <div className="d-flex align-center justify-center gap-10">
                          <If cond={brand.logo}>
                            <label
                              htmlFor={brand.id}
                              className={styles.customFileInput}
                            >
                              <div className={styles.logo}>
                                <img
                                  src={
                                    process.env.REACT_APP_ASSETS_URL +
                                    "/" +
                                    brand.logo
                                  }
                                  alt={brand.name}
                                />
                              </div>

                              <input
                                type="file"
                                id={brand.id}
                                accept="image/*"
                                onChange={(e) => handleSetImage(e, brand)}
                              />
                            </label>
                          </If>
                          <If cond={!brand.logo}>
                            <label
                              htmlFor={brand.id}
                              className={styles.customFileInput}
                            >
                              <span className={styles.plus}>
                                <svg
                                  width="8"
                                  height="8"
                                  viewBox="0 0 8 8"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M1.27971 1.27951C1.02896 1.27951 0.82638 1.48209 0.82638 1.73284V5.03934L1.78546 4.08025C2.00646 3.85925 2.36488 3.85925 2.5873 4.08025L3.54638 5.03934L5.41213 3.17359C5.63313 2.95259 5.99155 2.95259 6.21396 3.17359L7.17305 4.13267V1.73284C7.17305 1.48209 6.97046 1.27951 6.71971 1.27951H1.27971ZM0.82638 5.67967V6.26617C0.82638 6.51692 1.02896 6.7195 1.27971 6.7195H1.86621L3.22621 5.3595L2.26713 4.40042C2.22321 4.3565 2.15096 4.3565 2.10705 4.40042L0.82638 5.67967ZM5.8938 3.49375C5.84988 3.44984 5.77763 3.44984 5.73371 3.49375L2.50655 6.7195H6.71971C6.97046 6.7195 7.17305 6.51692 7.17305 6.26617V4.773L5.8938 3.49375ZM0.373047 1.73284C0.373047 1.23276 0.77963 0.826172 1.27971 0.826172H6.71971C7.2198 0.826172 7.62638 1.23276 7.62638 1.73284V6.26617C7.62638 6.76625 7.2198 7.17284 6.71971 7.17284H1.27971C0.77963 7.17284 0.373047 6.76625 0.373047 6.26617V1.73284ZM2.63971 2.41284C2.63971 2.35272 2.61583 2.29507 2.57332 2.25256C2.53082 2.21005 2.47316 2.18617 2.41305 2.18617C2.35293 2.18617 2.29528 2.21005 2.25277 2.25256C2.21026 2.29507 2.18638 2.35272 2.18638 2.41284C2.18638 2.47295 2.21026 2.53061 2.25277 2.57312C2.29528 2.61562 2.35293 2.6395 2.41305 2.6395C2.47316 2.6395 2.53082 2.61562 2.57332 2.57312C2.61583 2.53061 2.63971 2.47295 2.63971 2.41284ZM1.73305 2.41284C1.73305 2.23249 1.80469 2.05953 1.93221 1.93201C2.05974 1.80448 2.2327 1.73284 2.41305 1.73284C2.59339 1.73284 2.76635 1.80448 2.89388 1.93201C3.0214 2.05953 3.09305 2.23249 3.09305 2.41284C3.09305 2.59319 3.0214 2.76615 2.89388 2.89367C2.76635 3.0212 2.59339 3.09284 2.41305 3.09284C2.2327 3.09284 2.05974 3.0212 1.93221 2.89367C1.80469 2.76615 1.73305 2.59319 1.73305 2.41284Z"
                                    fill="#4C4C4C"
                                  />
                                </svg>
                              </span>

                              <input
                                type="file"
                                id={brand.id}
                                accept="image/*"
                                onChange={(e) => handleSetImage(e, brand)}
                              />
                            </label>
                          </If>
                          <If cond={!brand.logo}>
                            <InfoCircleGrey />
                          </If>

                          <If cond={brand.logo}>
                            <InfoCircleGreen />
                          </If>
                        </div>
                      </td>
                    </If>
                    <td
                      style={{
                        paddingLeft: shouldShowField(brands, "logo") ? 15 : 45,
                      }}
                    >
                      <Input
                        design="mini"
                        value={brand.name}
                        onChange={(e) =>
                          handleBrandChange(brand.id, "name", e.target.value)
                        }
                        filled={!!brand.name}
                      />
                    </td>
                    <If cond={shouldShowField(brands, "link")}>
                      <td>
                        <div className="d-flex align-center gap-10">
                          <Input
                            design="mini"
                            className="w-100"
                            value={brand.link}
                            onChange={(e) =>
                              handleBrandChange(
                                brand.id,
                                "link",
                                e.target.value
                              )
                            }
                            link
                            valid={validLinkRegex.test(brand.link)}
                          />
                          <Button
                            design="default-icon-mini-2"
                            onClick={() => {
                              setOpenCopyLink(true);
                              setSelectedBrand(brand);
                            }}
                            disabled={!brand.link}
                          >
                            <i className="fal fa-layer-group" />
                          </Button>
                        </div>
                      </td>
                    </If>
                    <If cond={shouldShowField(brands, "description")}>
                      <td>
                        <div className="d-flex align-center gap-10">
                          <div
                            className={styles.url}
                            onClick={() => {
                              setOpenDescription(true);
                              setSelectedBrand(brand);
                            }}
                          >
                            <span>{brand.description}</span>
                            <If cond={!brand.description}>
                              <InfoCircleGrey />
                            </If>

                            <If cond={brand.description}>
                              <InfoCircleGreen />
                            </If>
                          </div>
                          <Button
                            design="default-icon-mini-2"
                            onClick={() => {
                              setOpenCopyDescription(true);
                              setSelectedBrand(brand);
                            }}
                            disabled={!brand.description}
                          >
                            <i className="fal fa-layer-group" />
                          </Button>
                        </div>
                      </td>
                    </If>
                  </tr>
                )}
              />
            </tbody>
          </table>
        </div>

        <If cond={brands.length === 0}>
          <div className={styles.empty}>
            <i className="fal fa-store" />
            <p className={styles.title}>
              Você ainda não possui nenhuma marca cadastrada
            </p>
            <p className={styles.subtitle}>
              Através desta ferramenta, você pode registrar e configurar sua(s)
              loja(s).
            </p>
            <Button design="primary" onClick={() => setBrands([emptyBrand])}>
              Adicionar marca
            </Button>
          </div>
        </If>

        <If cond={brands.length > 0}>
          <div className={styles.footer}>
            <Button
              onClick={() => setBrands([...brands, emptyBrand])}
              className="gap-15"
            >
              <i className="fas fa-plus" />
              Adicionar marca
            </Button>
            <Button
              // disabled={getIncompleteObjects(brands).length > 0}
              onClick={save}
              design="primary"
            >
              Salvar alterações
            </Button>
          </div>
        </If>
      </div>

      <Drawer
        visible={openDescription}
        onClose={() => setOpenDescription(false)}
        title="Descrição"
        subtitle={environment?.brand?.name}
      >
        <Label required>Insira a descrição desta marca:</Label>
        <Textarea
          design="three"
          placeholder="Descrição"
          value={selectedBrand?.description}
          minHeight={150}
          onChange={(e) =>
            setSelectedBrand({ ...selectedBrand, description: e.target.value })
          }
        />
        <div className={styles.componentFooter}>
          <Button
            design="primary"
            className="w-100"
            onClick={() => {
              handleBrandChange(
                selectedBrand.id,
                "description",
                selectedBrand.description
              );
              setOpenDescription(false);
            }}
          >
            Salvar
          </Button>
        </div>
      </Drawer>

      <Drawer
        visible={openCopyDescription}
        onClose={() => setOpenCopyDescription(false)}
        title="Replicar dado / Descrição"
        subtitle={environment?.brand?.name}
      >
        <div className={styles.messageContainer}>
          <div className={styles.header}>
            <InfoCircle />

            <div className={styles.divider} />
            <p>
              Este dado configurado será replicado em todas as marcas
              selecionadas.
            </p>
          </div>
          <div className={styles.toCopy}>{selectedBrand?.description}</div>
        </div>

        <Items />

        <div className={styles.componentFooter}>
          <Button
            design="primary"
            className="w-100"
            disabled={!selectedBrand?.description}
            onClick={() => {
              const updated = updateObjectsByIds(
                brands,
                selectedItems,
                "description",
                selectedBrand?.description
              );

              setBrands(updated);
              setSelectedItems([]);
              setOpenCopyDescription(false);
            }}
          >
            Salvar
          </Button>
        </div>
      </Drawer>

      <Drawer
        visible={openCopyLink}
        onClose={() => setOpenCopyLink(false)}
        title="Replicar dado / Link"
        subtitle={environment?.brand?.name}
      >
        <div className={styles.messageContainer}>
          <div className={styles.header}>
            <InfoCircle />

            <div className={styles.divider} />
            <p>
              Este dado configurado será replicado em todas as marcas
              selecionadas.
            </p>
          </div>
          <div className={styles.toCopyLink}>
            <Link />
            <span>{selectedBrand?.link}</span>
          </div>
          <Items />
        </div>

        <div className={styles.componentFooter}>
          <Button
            design="primary"
            className="w-100"
            disabled={!selectedBrand?.link}
            onClick={() => {
              const updated = updateObjectsByIds(
                brands,
                selectedItems,
                "link",
                selectedBrand?.link
              );

              setBrands(updated);
              setSelectedItems([]);
              setOpenCopyLink(false);
            }}
          >
            Salvar
          </Button>
        </div>
      </Drawer>

      <Drawer
        visible={openSuccess}
        onClose={() => setOpenSuccess(false)}
        direction="center"
        showCloseButton={false}
      >
        <div className={styles.success}>
          <p className={styles.title}>Sucesso!</p>
          <p className={styles.subtitle}>
            Suas últimas alterações foram salvas.
          </p>
          <Button
            design="primary"
            className="w-100 mb-15"
            onClick={() => setOpenSuccess(false)}
          >
            Continuar aqui
          </Button>
          <Button className="w-100" onClick={() => navigate("/implantacao")}>
            Ir para tela inicial
          </Button>
        </div>
      </Drawer>

      <Drawer
        visible={openError}
        onClose={() => setOpenError(false)}
        direction="center"
        showCloseButton={false}
      >
        <div className={styles.success}>
          <p className={styles.title}>Erro ao salvar</p>
          <p className={styles.subtitle}>{errorMessage}</p>
          <Button className="w-100" onClick={() => setOpenError(false)}>
            Voltar
          </Button>
        </div>
      </Drawer>
    </div>
  );
}
